import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Calendar } from "primereact/calendar";

import {
  completeRange,
  PERIOD_PRESETS,
  PeriodPreset,
  PeriodSelection,
  presetSelection,
  selectionDates,
  startOfDay,
} from "../../utilities/reportRange";
import styles from "./PeriodFilter.module.css";

export interface PeriodFilterProps {
  /**
   * Seleção PENDENTE (o que o controle mostra). `null` = o usuário escolheu
   * "Personalizado" mas ainda não fechou as duas datas — a tela deve desabilitar
   * o "Aplicar" nesse estado.
   */
  value: PeriodSelection | null;
  onChange: (next: PeriodSelection | null) => void;
  /**
   * Rótulo da janela efetivamente carregada. Exibido SÓ fora do modo customizado — ali o
   * próprio seletor de datas é o indicador, para não haver dois na mesma linha.
   */
  rangeLabel: string;
  /** Rótulo do grupo — a tela passa o seu `t("fPeriod")`. */
  label: string;
  /** Rótulos dos presets — a tela passa `t("preset7")`, `t("preset14")`, `t("preset30")`. */
  presetLabels: Record<PeriodPreset, string>;
  /** Última data selecionável. Default: hoje (não há dado no futuro). */
  maxDate?: Date;
  className?: string;
}

/**
 * Filtro de período das telas de relatório: os presets de janela rolante
 * (7/14/30 dias) mais um segmento "Personalizado" que abre um seletor de
 * intervalo.
 *
 * Duas regras que o componente garante para quem consome:
 *
 * 1. Nunca emite um intervalo pela metade. O `<Calendar selectionMode="range">`
 *    devolve `[Date, null]` depois do primeiro clique, e mandar `to` sem `from`
 *    faz o backend responder 400. Enquanto o par não fecha, o `onChange` recebe
 *    `null` e a tela desabilita o "Aplicar" — que é o único sinal disso, de
 *    propósito: um texto de aviso abaixo do controle variava a altura do
 *    container a cada clique, e o placeholder do campo já diz o que fazer.
 * 2. A seleção só troca de fato quando está completa — trocar para
 *    "Personalizado" não descarta a janela já carregada.
 *
 * O locale do datepicker é passado explicitamente em vez de depender só do
 * `locale()` global do PrimeReact: aquele roda no import de
 * `utilities/primeReactDefaultTranslations.tsx`, antes deste componente montar, e
 * na prática o painel abria com os meses em inglês. Atenção ao valor — os locales
 * registrados lá se chamam "pt" e "en", e NÃO "pt-BR"/"en-US" (que é o que as
 * telas usam para o Intl); passar a variante longa não resolve e cai no inglês.
 */
const PeriodFilter: React.FC<PeriodFilterProps> = ({
  value,
  onChange,
  rangeLabel,
  label,
  presetLabels,
  maxDate,
  className,
}) => {
  const { t, i18n } = useTranslation("general");
  const calendarLocale = i18n.language?.toLowerCase().startsWith("en") ? "en" : "pt";

  const [customMode, setCustomMode] = useState<boolean>(value?.kind === "custom");
  const [draft, setDraft] = useState<Date[] | null>(() => {
    if (value?.kind !== "custom") return null;
    const { from, to } = selectionDates(value);
    return [from, to];
  });

  // A tela pode reverter a seleção por fora (o "Limpar filtros" volta para 30
  // dias) — o controle acompanha em vez de ficar preso no modo customizado.
  useEffect(() => {
    if (value?.kind === "preset") {
      setCustomMode(false);
      setDraft(null);
    }
  }, [value]);

  const activePreset = value?.kind === "preset" && !customMode ? value.preset : null;

  const selectPreset = (preset: PeriodPreset): void => {
    setCustomMode(false);
    setDraft(null);
    onChange(presetSelection(preset));
  };

  const openCustom = (): void => {
    setCustomMode(true);
    // Sem par completo ainda → a tela desabilita o "Aplicar".
    onChange(completeRange(draft) ? value : null);
  };

  const changeDraft = (next: unknown): void => {
    const dates = Array.isArray(next) ? (next as Date[]) : null;
    setDraft(dates);
    const range = completeRange(dates);
    onChange(range ? { kind: "custom", from: range.from, to: range.to } : null);
  };


  return (
    <div className={`${styles.wrap} ${className ?? ""}`}>
      <span className={styles.fieldLabel}>{label}</span>

      <div className={styles.periodRow}>
        {/* UM indicador de datas por linha. No modo customizado o seletor OCUPA O LUGAR do chip
            de texto em vez de aparecer ao lado: dois indicadores de data competindo na mesma
            linha confundia qual deles valia. Fora do modo customizado o chip volta, porque aí
            a janela é derivada do preset e não há nada para escolher. */}
        {customMode ? (
          <Calendar
            value={draft}
            onChange={(e) => changeDraft(e.value)}
            selectionMode="range"
            locale={calendarLocale}
            dateFormat="dd/mm/yy"
            placeholder={t("periodCustomPlaceholder")}
            maxDate={maxDate ?? startOfDay(new Date())}
            readOnlyInput
            showIcon
            // Um mês só. Dois meses lado a lado dobravam a largura do painel e a leitura piorava
            // mais do que a navegação melhorava — as setas de mês resolvem o intervalo que cruza
            // a virada.
            numberOfMonths={1}
            hideOnRangeSelection
            className={styles.calendar}
            inputClassName={styles.calendarInput}
            aria-label={t("periodCustomLabel")}
          />
        ) : (
          <span className={styles.rangeLabel}>
            <span className="material-symbols-outlined" aria-hidden="true">
              calendar_month
            </span>
            <span>{rangeLabel}</span>
          </span>
        )}

        <div className={styles.segmented} role="group" aria-label={label}>
          {PERIOD_PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => selectPreset(preset)}
              aria-pressed={activePreset === preset}
              className={`${styles.segBtn} ${
                activePreset === preset ? styles.segBtnActive : ""
              }`}
            >
              {presetLabels[preset]}
            </button>
          ))}
          <button
            type="button"
            onClick={openCustom}
            aria-pressed={customMode}
            className={`${styles.segBtn} ${customMode ? styles.segBtnActive : ""}`}
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              date_range
            </span>
            {t("periodCustom")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PeriodFilter;
